import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { Ref, useImperativeHandle, useState } from "react";
import PickupAndRefillLocation from "../../../PickupAndRefillLocation/PickupAndRefillLocation";
import styles from "./PickupAndRefillLocationCreatorFragment.module.css";
import EmptyObject from "@coworker/apprestructured/src/task-management/types/EmptyObject";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { AddLocationTab } from "@coworker/apprestructured/src/task-management/components/AddLocation/AddLocationModal";
import { ItemNoAndType } from "@coworker/apprestructured/src/shared/types/item";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { useFilteredSalesLocations } from "@coworker/apprestructured/src/task-management/services/useSalesLocations";
import {
  Location,
  LocationChange,
} from "@coworker/apprestructured/src/task-management/types/SalesLocation";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";

export type PickupAndRefillLocationCreatorFragmentConfigProps = EmptyObject;

type PickupAndRefillLocationCreatorFragmentProps = {
  configProps: PickupAndRefillLocationCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function PickupAndRefillLocationCreatorFragment({
  commonProps,
  ref,
}: PickupAndRefillLocationCreatorFragmentProps) {
  const { blockInput, task, onTaskChange, showValidation, taskType } =
    commonProps;

  const pickupLocation = validateInitialDetailsFieldValue<Location>(
    getByPath(task, "details.pickupLocation"),
    "details.pickupLocation",
    taskType
  );

  const refillLocation = validateInitialDetailsFieldValue<Location>(
    getByPath(task, "details.refillLocation"),
    "details.refillLocation",
    taskType
  );

  const itemNoAndType = validateInitialDetailsFieldValue<ItemNoAndType>(
    getByPath(task, "details.item"),
    "details.item",
    taskType
  );

  const [activeAddLocationTab, setActiveAddLocationTab] =
    useState<AddLocationTab>("locations");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [editingLocation, setEditingLocation] = useState<
    "pickup" | "refill" | null
  >(null);

  const {
    isLoadingLocations,
    filteredLocations,
    totalAssignedSalesSpaceQuantity,
  } = useFilteredSalesLocations(
    activeAddLocationTab === "locations" ? itemNoAndType : null,
    searchQuery
  );

  const handleConfirmLocation = ({ slid, customLocation }: LocationChange) => {
    if (!editingLocation) {
      return;
    }
    const field =
      editingLocation === "pickup"
        ? "details.pickupLocation"
        : "details.refillLocation";

    onTaskChange(
      setByPath(field, {
        slid: slid ?? "",
        custom: customLocation ?? "",
      })
    );

    setEditingLocation(null);
  };

  const currentLocation =
    editingLocation === "pickup"
      ? pickupLocation
      : editingLocation === "refill"
        ? refillLocation
        : null;

  const currentLocationSlid =
    currentLocation && !isLoading(currentLocation) ? currentLocation.slid : "";
  const currentCustomLocation =
    currentLocation && !isLoading(currentLocation)
      ? currentLocation.custom
      : "";

  const pickupLocationText =
    !isLoading(pickupLocation) && pickupLocation
      ? pickupLocation.slid || pickupLocation.custom || ""
      : "";
  const refillLocationText =
    !isLoading(refillLocation) && refillLocation
      ? refillLocation.slid || refillLocation.custom || ""
      : "";

  const isPickUpAndRefillLocationSame =
    pickupLocationText.length > 0 && pickupLocationText === refillLocationText;

  const hasPickupLocation = pickupLocationText.length > 0;
  const hasRefillLocation = refillLocationText.length > 0;
  const hasRequiredLocations = hasPickupLocation && hasRefillLocation;

  const valid =
    !isLoading(pickupLocation) &&
    !isLoading(refillLocation) &&
    pickupLocation !== null &&
    refillLocation !== null &&
    hasRequiredLocations &&
    !isPickUpAndRefillLocationSame;

  useImperativeHandle(ref, () => ({ valid }));

  if (
    isLoading(pickupLocation) ||
    isLoading(refillLocation) ||
    isLoading(itemNoAndType)
  ) {
    return <LoadingCreatorFragment />;
  }

  return (
    <div className={styles["root"]}>
      <PickupAndRefillLocation
        pickupLocation={pickupLocationText}
        refillLocation={refillLocationText}
        isPickUpAndRefillLocationSame={isPickUpAndRefillLocationSame}
        showValidation={showValidation}
        onPickupClick={() => setEditingLocation("pickup")}
        onRefillClick={() => setEditingLocation("refill")}
        disabled={blockInput}
        locationModalProps={{
          visible: editingLocation !== null,
          onClose: () => setEditingLocation(null),
          onTabChanged: setActiveAddLocationTab,
          itemNoAndType,
          slid: currentLocationSlid,
          customLocation: currentCustomLocation,
          onConfirmClick: handleConfirmLocation,
          isLoadingLocations,
          filteredLocations,
          totalAssignedSalesSpaceQuantity,
          searchQuery,
          setSearchQuery,
        }}
      />
    </div>
  );
}

export default PickupAndRefillLocationCreatorFragment;
